import { useMemo } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  PolarComponent,
  TitleComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import type { EChartsCoreOption } from "echarts/core";

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  PolarComponent,
  TitleComponent,
  SVGRenderer,
]);

/** Fonte única dos gráficos do relatório — a mesma do restante do PDF. */
export const CHART_FONT_FAMILY =
  "Inter, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/**
 * Renderiza um gráfico ECharts como SVG estático (modo SSR do ECharts).
 *
 * Sem canvas, sem animação e sem medir o DOM: o mesmo SVG serve para o
 * preview e para a impressão, sai vetorial no PDF e não depende de o
 * contêiner estar visível — a impressão monta o relatório num portal oculto,
 * onde um gráfico interativo teria largura zero.
 */
export function renderChartSvg(
  option: EChartsCoreOption,
  width: number,
  height: number
): string {
  const chart = echarts.init(null, null, {
    renderer: "svg",
    ssr: true,
    width,
    height,
  });
  try {
    chart.setOption({
      animation: false,
      textStyle: { fontFamily: CHART_FONT_FAMILY },
      ...option,
    });
    return chart.renderToSVGString();
  } finally {
    chart.dispose();
  }
}

interface EChartSvgProps {
  option: EChartsCoreOption;
  width: number;
  height: number;
  /** Texto alternativo — o SVG em si não é lido por leitores de tela. */
  ariaLabel: string;
  className?: string;
}

export function EChartSvg({
  option,
  width,
  height,
  ariaLabel,
  className,
}: EChartSvgProps) {
  const svg = useMemo(
    () => renderChartSvg(option, width, height),
    [option, width, height]
  );

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={{ width: "100%", maxWidth: width }}
      // O SVG é gerado pelo ECharts a partir de dados do próprio relatório.
      // Ele já traz viewBox; trocamos só a largura/altura fixas para que
      // escale com a coluna da página.
      dangerouslySetInnerHTML={{
        __html: svg.replace(
          /^<svg width="\d+" height="\d+"/,
          '<svg width="100%" style="display:block;height:auto"'
        ),
      }}
    />
  );
}
